// @ts-check

/** @file Adapts AwesomeTasks' project-local Sync rows to Velocious LocalMutationLog storage. */

import LocalMutationLog from "velocious/build/src/sync/local-mutation-log.js"

import Sync from "../models/sync.js"
import {PROJECT_REPLICA_DATABASE_IDENTIFIER} from "./project-replica-contract.js"

/** @import TenantHandle from "velocious/build/src/tenants/tenant-handle.js" */
/** @import {LocalMutationLogRecord, LocalMutationLogRecordsOptions} from "velocious/build/src/sync/local-mutation-log.js" */
/** @import ModelClassQuery from "velocious/build/src/database/query/model-class-query.js" */

const MUTATION_LOG_STORAGE_KEY = "awesome-tasks.project-sync.intent"

/** Row-oriented durable adapter bound to one immutable project replica. */
class ProjectReplicaMutationLogStorage {
  /**
   * Creates storage bound to one captured project handle.
   * @param {TenantHandle} handle Captured project handle.
   */
  constructor(handle) {
    this.handle = handle
    /** @type {Map<string, Map<string, LocalMutationLogRecord>>} */
    this.recordsByStorageKey = new Map()
    /** @type {Promise<void>} */
    this.pendingWrites = Promise.resolve()
    /** @type {Promise<unknown>} */
    this.observedWriteFailure = Promise.resolve(null)
  }

  /** Loads all durable intent rows before SyncClient can enter a tenant operation. */
  async initialize() {
    await this.withSyncs(async (syncs) => {
      for (const row of await syncs.toArray()) {
        const storageKey = row.command()
        if (!storageKey?.startsWith(MUTATION_LOG_STORAGE_KEY)) continue
        const record = rowRecord(row)
        if (record) this.storageRecords(storageKey).set(record.id, record)
      }
    })
  }

  /**
   * Adds a durable intent to the owned partition.
   * @param {string} storageKey Partition key.
   * @param {LocalMutationLogRecord} record Durable record.
   */
  async appendRecord(storageKey, record) {
    this.storageRecords(storageKey).set(record.id, cloneRecord(record))
    this.enqueueWrite(async (syncs) => { await syncs.create(syncAttributes(storageKey, record)) })
  }

  /**
   * Removes resolved intents from the owned partition.
   * @param {string} storageKey Partition key.
   * @param {string[]} ids Record ids.
   */
  async deleteRecords(storageKey, ids) {
    const records = this.storageRecords(storageKey)
    for (const id of ids) records.delete(id)
    this.enqueueWrite(async (syncs) => {
      for (const id of ids) {
        const row = await syncs.findBy({command: storageKey, id})
        if (row) await row.destroy()
      }
    })
  }

  /**
   * Finds the next monotonically increasing local sequence.
   * @param {string} storageKey Partition key.
   * @returns {Promise<number>} Value greater than every stored sequence.
   */
  async nextSequence(storageKey) {
    const greatest = [...this.storageRecords(storageKey).values()]
      .reduce((value, record) => Math.max(value, record.sequence), 0)

    return greatest + 1
  }

  /**
   * Reads one isolated copy of an intent.
   * @param {string} storageKey Partition key.
   * @param {string} id Durable mutation identity.
   * @returns {Promise<LocalMutationLogRecord | null>} Stored record.
   */
  async record(storageKey, id) {
    const record = this.storageRecords(storageKey).get(id)

    return record ? cloneRecord(record) : null
  }

  /**
   * Reads ordered isolated copies of matching intents.
   * @param {string} storageKey Partition key.
   * @param {LocalMutationLogRecordsOptions} [options] Query options.
   * @returns {Promise<LocalMutationLogRecord[]>} Stored records.
   */
  async records(storageKey, options = {}) {
    const statuses = options.statuses ? new Set(options.statuses) : null

    return [...this.storageRecords(storageKey).values()]
      .filter((record) => !statuses || statuses.has(record.status))
      .sort((left, right) => left.sequence - right.sequence)
      .map(cloneRecord)
  }

  /**
   * Replaces one existing durable intent.
   * @param {string} storageKey Partition key.
   * @param {LocalMutationLogRecord} record Replacement.
   * @rejects {Error} When the durable intent has disappeared.
   */
  async updateRecord(storageKey, record) {
    if (!this.storageRecords(storageKey).has(record.id)) {
      throw new Error(`Project mutation log record disappeared: ${record.id}`)
    }
    this.storageRecords(storageKey).set(record.id, cloneRecord(record))
    this.enqueueWrite(async (syncs) => {
      const row = await syncs.findBy({command: storageKey, id: record.id})

      if (!row) throw new Error(`Project mutation log record disappeared: ${record.id}`)
      row.assign(syncAttributes(storageKey, record))
      await row.save()
    })
  }

  /**
   * Awaits replay acknowledgement writes after SyncClient releases its tenant operation.
   * @returns {Promise<void>} Durable write completion.
   * @rejects {Error} When a queued row write or database flush fails.
   */
  async flushPendingWrites() {
    const failure = await this.observedWriteFailure
    if (failure) throw failure
    await this.handle.flush({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})
  }

  /**
   * Returns the mutable cache owned by this storage adapter.
   * @param {string} storageKey Partition key.
   * @returns {Map<string, LocalMutationLogRecord>} Mutable owned partition.
   */
  storageRecords(storageKey) {
    let records = this.recordsByStorageKey.get(storageKey)
    if (!records) {
      records = new Map()
      this.recordsByStorageKey.set(storageKey, records)
    }

    return records
  }

  /**
   * Serializes row writes. Replay updates intentionally do not await the second
   * tenant checkout held behind SyncClient's active operation; the coordinator
   * drains them immediately after that operation completes.
   * @param {(syncs: ModelClassQuery<typeof Sync>) => Promise<void>} callback Row mutation.
   */
  enqueueWrite(callback) {
    const write = this.pendingWrites.then(async () => await this.withSyncs(callback))

    this.pendingWrites = write
    this.observedWriteFailure = write.then(() => null, (error) => error)
  }

  /**
   * Runs one operation against this replica's Sync model.
   * @template Result - Operation result.
   * @param {(syncs: ModelClassQuery<typeof Sync>) => Promise<Result>} callback Operation callback.
   * @returns {Promise<Result>} Callback result.
   */
  async withSyncs(callback) {
    return await this.handle.databaseOperation({
      databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER,
      name: "Project replica mutation log"
    }, async (operation) => await callback(operation.forModel(Sync)))
  }
}

/**
 * Creates the base log that SyncClient partitions by physical database identity.
 * @param {{handle: TenantHandle, idGenerator?: () => string, now?: () => Date}} args Adapter inputs.
 * @returns {Promise<{flushPendingWrites: () => Promise<void>, mutationLog: LocalMutationLog}>} Initialized log and durability drain.
 */
export async function createProjectReplicaMutationLog({handle, idGenerator = syncUuid, now = () => new Date()}) {
  const storage = new ProjectReplicaMutationLogStorage(handle)

  await storage.initialize()

  return {
    flushPendingWrites: async () => await storage.flushPendingWrites(),
    mutationLog: new LocalMutationLog({
      idGenerator,
      now,
      storage,
      storageKey: MUTATION_LOG_STORAGE_KEY
    })
  }
}

/**
 * Maps one framework log record to the local Sync schema.
 * @param {string} storageKey Partition key.
 * @param {LocalMutationLogRecord} record Log record.
 * @returns {Record<string, unknown>} Local Sync attributes.
 * @throws {Error} When a framework mutation lacks a resource id.
 */
function syncAttributes(storageKey, record) {
  const resourceId = record.mutation.payload?.resourceId

  if (typeof resourceId !== "string" || resourceId.length === 0) throw new Error("Project mutation log requires a resourceId")

  return {
    command: storageKey,
    data: JSON.parse(JSON.stringify(record)),
    failureDetails: null,
    failureReason: record.status === "conflict" || record.status === "rejected" ? record.status : null,
    id: record.id,
    predecessorMutationId: record.dependencies.at(-1)?.clientMutationId ?? null,
    resourceId,
    resourceType: record.mutation.model,
    sequence: record.sequence,
    // Keep row-oriented conflict intents distinct from Velocious's legacy
    // one-row queue, whose replay selector intentionally owns state=pending.
    state: `intent-${record.status}`,
    syncType: String(record.mutation.payload?.syncType || record.mutation.operation)
  }
}

/**
 * Restores one framework log record from a local Sync row.
 * @param {InstanceType<typeof Sync> | null | undefined} row Stored row.
 * @returns {LocalMutationLogRecord | null} Restored log record.
 * @throws {Error} When the row payload is malformed.
 */
function rowRecord(row) {
  if (!row) return null
  const rawData = row.data()
  const data = typeof rawData === "string" ? JSON.parse(rawData) : rawData

  if (!data || typeof data !== "object" || Array.isArray(data)) {
    throw new Error(`Project mutation log row ${row.id()} has invalid data`)
  }

  return /** @type {LocalMutationLogRecord} */ (JSON.parse(JSON.stringify(data)))
}

/**
 * Generates an RFC 4122 v4 id for durable local identities.
 * @returns {string} New UUID.
 */
export function syncUuid() {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === "function") return globalThis.crypto.randomUUID()

  const bytes = new Uint8Array(16)
  if (globalThis.crypto && typeof globalThis.crypto.getRandomValues === "function") {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0")).join("")

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * Clones a framework log record at the storage boundary.
 * @param {LocalMutationLogRecord} record Source record.
 * @returns {LocalMutationLogRecord} JSON-safe clone.
 */
function cloneRecord(record) {
  return /** @type {LocalMutationLogRecord} */ (JSON.parse(JSON.stringify(record)))
}
